import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Package, ArrowRightLeft, Clock, CheckCircle, XCircle, DollarSign, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, Trash2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_review: { label: 'PENDING', color: 'bg-zinc-500', textColor: 'text-zinc-400', icon: Clock },
  interested: { label: 'INTERESTED', color: 'bg-teal-500', textColor: 'text-teal-400', icon: CheckCircle },
  offer_made: { label: 'OFFER SENT', color: 'bg-yellow-500', textColor: 'text-yellow-400', icon: DollarSign },
  accepted: { label: 'ACCEPTED', color: 'bg-green-500', textColor: 'text-green-400', icon: CheckCircle },
  declined: { label: 'DECLINED', color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle }
};

// Custom Confirm Modal - styled to match site
const ConfirmModal = ({ isOpen, onConfirm, onCancel, message }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90">
      <div className="bg-zinc-900 border border-red-500/50 p-6 max-w-sm w-full">
        <p className="font-mono text-sm mb-6 text-white uppercase text-center">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm uppercase hover:bg-zinc-700 transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 bg-red-500 text-white font-mono font-bold text-sm uppercase hover:bg-red-600 transition-colors"
          >
            YES, DELETE
          </button>
        </div>
      </div>
    </div>
  );
};

// Image Lightbox with swipe/carousel support
const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goPrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
      if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(prev => prev - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose]);
  
  if (!images || images.length === 0) return null;
  
  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 p-2 text-white z-10 bg-zinc-800/50 hover:bg-zinc-700"
      >
        <X size={24} />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white font-mono text-sm bg-zinc-800/50 px-3 py-1">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </>
      )}

      {/* Main image with touch support */}
      <div
        className="w-full h-full flex items-center justify-center p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={getImageUrl(images[currentIndex])}
          alt=""
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-teal-500' : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminSubmissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0 });
  const [lightboxData, setLightboxData] = useState(null); // { images: [], initialIndex: 0 }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // submission id to delete

  const getToken = () => localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');

  const fetchSubmissions = useCallback(async () => {
    const token = getToken();
    try {
      let url = `${API_URL}/api/admin/submissions`;
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
        setStats({ total: data.total, pending: data.pending_review });
      }
    } catch (err) {
      toast.error('FAILED TO LOAD');
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleSearch = () => setSearchQuery(searchInput.toUpperCase());

  const updateStatus = async (submissionId, newStatus, itemId = null, offerAmount = null) => {
    const token = getToken();
    try {
      let url = `${API_URL}/api/admin/submissions/${submissionId}/status`;
      if (itemId) url += `?item_id=${itemId}`;

      const body = { status: newStatus };
      if (offerAmount) body.counter_offer = parseFloat(offerAmount);

      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success('UPDATED');
        fetchSubmissions();
      } else {
        toast.error('FAILED');
      }
    } catch (err) {
      toast.error('FAILED');
    }
  };

  const deleteSubmission = async (submissionId) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        // Clear any state that references the deleted submission BEFORE the
        // refetch — otherwise stale ids in expandedSubmission/lightboxData can
        // cause render-time crashes (the lightbox tries to render images from
        // a row that no longer exists in `submissions`).
        if (expandedSubmission === submissionId) setExpandedSubmission(null);
        if (lightboxData && lightboxData.submissionId === submissionId) setLightboxData(null);
        toast.success('DELETED', { duration: 2000 });
        fetchSubmissions();
      } else {
        // Surface backend failures (FastAPI 422 returns detail as an array of
        // objects — must stringify before toast or React/sonner crashes).
        let detail = `Status ${res.status}`;
        try { const body = await res.json(); if (body?.detail) detail = body.detail; } catch { /* not json */ }
        toast.error(typeof detail === 'string' ? detail : 'DELETE FAILED');
      }
    } catch (err) {
      toast.error('FAILED');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const markReceived = async (submissionId, itemId = null, received = true, silent = false) => {
    const token = getToken();
    try {
      let url = `${API_URL}/api/admin/submissions/${submissionId}/mark-received?received=${received}&silent=${silent}`;
      if (itemId) url += `&item_id=${itemId}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(received ? (silent ? 'MARKED RECEIVED (NO EMAIL)' : 'MARKED RECEIVED · EMAIL SENT') : 'UNMARKED');
        fetchSubmissions();
      } else {
        toast.error('FAILED');
      }
    } catch (err) {
      toast.error('FAILED');
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.user_email?.toLowerCase().includes(query) ||
      sub.user_name?.toLowerCase().includes(query) ||
      sub.id?.toLowerCase().includes(query) ||
      sub.product_name?.toLowerCase().includes(query)
    );
  });

  // Navigate to admin dashboard with pre-filled product form data
  const createProductFromItem = (item, costPaid) => {
    const productData = {
      name: item.name || '',
      description: item.description || '',
      category: item.category || '',
      brand: item.brand || '',
      images: item.images || [],
      cost: costPaid?.toString() || '0'
    };
    // Store in sessionStorage and navigate to admin with flag
    sessionStorage.setItem('prefill_product', JSON.stringify(productData));
    navigate('/admin?tab=products&action=create');
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending_review;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-medium ${config.color} text-black`}>
        <Icon size={12} />{config.label}
      </span>
    );
  };

  // Action buttons - ACCEPT, DECLINE, COUNTER
  const ActionButtons = ({ currentStatus, onAction, submissionId, itemId = null, askingPrice = 0, currentCashOffer = null, isTrade = false }) => {
    const [showCounter, setShowCounter] = useState(false);
    const [offerAmount, setOfferAmount] = useState('');

    const sendOffer = () => {
      if (!offerAmount || parseFloat(offerAmount) <= 0) {
        toast.error('ENTER AMOUNT');
        return;
      }
      onAction(submissionId, 'offer_made', itemId, offerAmount);
      setShowCounter(false);
      setOfferAmount('');
    };

    if (showCounter) {
      const ref = currentCashOffer != null ? currentCashOffer : askingPrice;
      const newAmount = parseFloat(offerAmount);
      const delta = !isNaN(newAmount) && currentCashOffer != null ? newAmount - currentCashOffer : null;
      return (
        <div className="flex flex-col gap-2 w-full">
          {currentCashOffer != null && (
            <div className="bg-zinc-800 border border-zinc-700 px-3 py-2 flex items-center justify-between gap-3 text-xs font-mono">
              <span className="text-zinc-400">{isTrade ? 'THEIR CASH OFFER' : 'THEIR ASKING PRICE'}</span>
              <span className="text-green-400 font-bold text-sm" data-testid="counter-current-offer">
                ${currentCashOffer.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <span className="text-sm font-mono text-zinc-400">$</span>
              <input
                type="number"
                step="0.01"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder={ref ? ref.toFixed(2) : '0.00'}
                className="w-24 bg-zinc-700 border border-zinc-600 px-2 py-2 text-sm font-mono"
                autoFocus
                data-testid="counter-offer-input"
              />
              {delta != null && delta !== 0 && (
                <span className={`text-xs font-mono font-bold ${delta > 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="counter-offer-delta">
                  {delta > 0 ? '+' : ''}${delta.toFixed(2)}
                </span>
              )}
            </div>
            <button onClick={sendOffer} className="px-4 py-2 bg-yellow-500 text-black text-sm font-mono font-bold" data-testid="counter-offer-send">
              SEND COUNTER
            </button>
            <button onClick={() => { setShowCounter(false); setOfferAmount(''); }} className="px-3 py-2 bg-zinc-700 text-zinc-400 text-sm font-mono">
              CANCEL
            </button>
          </div>
        </div>
      );
    }

    if (currentStatus === 'pending_review') {
      return (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => onAction(submissionId, 'accepted', itemId)}
            className="px-4 py-2 bg-green-500 text-black text-sm font-mono font-bold"
          >
            ACCEPT
          </button>
          <button
            onClick={() => setShowCounter(true)}
            className="px-4 py-2 bg-yellow-500 text-black text-sm font-mono font-bold"
          >
            COUNTER
          </button>
          <button
            onClick={() => onAction(submissionId, 'declined', itemId)}
            className="px-4 py-2 bg-red-500 text-white text-sm font-mono font-bold"
          >
            DECLINE
          </button>
        </div>
      );
    }

    if (currentStatus === 'offer_made') {
      return (
        <div className="bg-yellow-500/20 border border-yellow-500/50 px-3 py-2">
          <span className="text-sm font-mono text-yellow-400">WAITING FOR USER RESPONSE...</span>
        </div>
      );
    }

    if (currentStatus === 'accepted') {
      return (
        <div className="bg-green-500/20 border border-green-500/50 px-3 py-2">
          <span className="text-sm font-mono text-green-400">✓ DEAL ACCEPTED</span>
        </div>
      );
    }

    if (currentStatus === 'declined') {
      return (
        <div className="bg-red-500/20 border border-red-500/50 px-3 py-2">
          <span className="text-sm font-mono text-red-400">✗ DECLINED</span>
        </div>
      );
    }

    return null;
  };

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('COPIED!', { duration: 1500 });
  };

  const calculateProfit = (sub) => {
    if (sub.type !== 'trade') return null;
    const totalOffer = sub.total_offer_value || 0;
    const productPrice = sub.product_price || 0;
    if (productPrice === 0) return { profit: 0, profitPercent: 0 };
    const profit = totalOffer - productPrice;
    const profitPercent = ((totalOffer / productPrice) - 1) * 100;
    return { profit, profitPercent };
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {lightboxData && (
        <ImageLightbox 
          images={lightboxData.images} 
          initialIndex={lightboxData.initialIndex} 
          onClose={() => setLightboxData(null)} 
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        message="ARE YOU SURE YOU WANT TO DELETE THIS SUBMISSION?"
        onConfirm={() => deleteSubmission(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-black border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-lg font-bold font-mono">SUBMISSIONS</h1>
              <p className="text-xs text-zinc-500 font-mono">{stats.pending} PENDING</p>
            </div>
          </div>
          <button onClick={fetchSubmissions} className="p-2">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto">
          {['all', 'sell', 'trade'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-mono ${filter === f ? 'bg-teal-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="SEARCH..."
            className="flex-1 bg-zinc-800 border border-zinc-700 px-4 py-3 text-sm font-mono uppercase placeholder-zinc-500"
          />
          <button onClick={handleSearch} className="px-5 py-3 bg-teal-500 text-black font-mono font-bold">
            <Search size={18} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-zinc-900 border border-zinc-800 p-3">
            <p className="text-xl font-bold font-mono">{stats.total}</p>
            <p className="text-xs text-zinc-500 font-mono">TOTAL</p>
          </div>
          <div className="bg-zinc-900 border border-yellow-500/30 p-3">
            <p className="text-xl font-bold font-mono text-yellow-400">{stats.pending}</p>
            <p className="text-xs text-zinc-500 font-mono">PENDING</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-3">
            <p className="text-xl font-bold font-mono">{submissions.filter(s => s.type === 'sell').length}</p>
            <p className="text-xs text-zinc-500 font-mono">SELL</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-3">
            <p className="text-xl font-bold font-mono">{submissions.filter(s => s.type === 'trade').length}</p>
            <p className="text-xs text-zinc-500 font-mono">TRADE</p>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center py-12 text-zinc-500 font-mono">LOADING...</p>
        ) : filteredSubmissions.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto mb-3 text-zinc-600" size={40} />
            <p className="font-mono text-zinc-500">NO SUBMISSIONS</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSubmissions.map(sub => {
              const profitData = calculateProfit(sub);
              
              return (
                <div key={sub.id} className="bg-zinc-900 border border-zinc-800 overflow-hidden">
                  {/* Header */}
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-3 min-w-0 flex-1 basis-full sm:basis-auto">
                        <div className={`p-2 shrink-0 ${sub.type === 'trade' ? 'bg-purple-500/20 text-purple-400' : 'bg-teal-500/20 text-teal-400'}`}>
                          {sub.type === 'trade' ? <ArrowRightLeft size={18} /> : <Package size={18} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-sm font-medium truncate">
                            {sub.user_name?.toUpperCase() || sub.user_email?.split('@')?.[0]?.toUpperCase() || 'UNKNOWN'}
                          </p>
                          <p className="text-xs text-zinc-500 font-mono truncate">
                            {sub.user_email} • {sub.id}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 flex-wrap justify-end ml-auto sm:ml-0">
                        {sub.type === 'trade' && profitData && (
                          <div className={`text-right px-2 py-1 ${profitData.profit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            <p className={`font-mono text-sm font-bold ${profitData.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {profitData.profit >= 0 ? '+' : ''}${profitData.profit.toFixed(0)}
                            </p>
                            <p className={`text-xs font-mono ${profitData.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {profitData.profitPercent >= 0 ? '+' : ''}{profitData.profitPercent.toFixed(0)}%
                            </p>
                          </div>
                        )}
                        
                        {sub.type === 'sell' ? (
                          <span className="text-sm text-zinc-400 font-mono">{sub.items?.length} ITEMS</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            {renderStatusBadge(sub.status)}
                            {sub.status === 'accepted' && (
                              <span
                                className={`px-2 py-1 text-[10px] font-mono font-bold border ${sub.trade_received ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'}`}
                                data-testid={`trade-receipt-pill-${sub.id}`}
                                title={sub.trade_received_at ? new Date(sub.trade_received_at).toLocaleString() : ''}
                              >
                                {sub.trade_received
                                  ? `✓ RECEIVED ${sub.trade_received_at ? '· ' + new Date(sub.trade_received_at).toLocaleDateString(undefined,{month:'short',day:'numeric'}) : ''}`
                                  : 'PENDING RECEIVING'}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {expandedSubmission === sub.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>
                    
                    {sub.type === 'trade' && (
                      <div className="mt-3 flex items-center gap-2 text-xs font-mono">
                        <div className="flex items-center gap-2 bg-zinc-800 px-2 py-1">
                          {sub.product_image && (
                            <img src={getImageUrl(sub.product_image)} alt="" className="w-8 h-8 object-cover" />
                          )}
                          <span className="text-zinc-300 truncate max-w-[140px]">
                            {sub.product_name?.toUpperCase()}
                            {sub.variant_name && <span className="text-purple-400"> — {sub.variant_name.toUpperCase()}</span>}
                          </span>
                        </div>
                        <span className="text-zinc-600">←</span>
                        <div className="flex items-center gap-2 bg-purple-500/20 px-2 py-1">
                          {sub.item?.images?.[0] && (
                            <img src={getImageUrl(sub.item.images[0])} alt="" className="w-8 h-8 object-cover" />
                          )}
                          <span className="text-purple-400 truncate max-w-[100px]">{sub.item?.name?.toUpperCase()}</span>
                        </div>
                        <span className="text-green-400 font-bold">+${(sub.cash_offer ?? 0).toFixed(0)}</span>
                      </div>
                    )}
                  </div>

                  {/* Expanded */}
                  {expandedSubmission === sub.id && (
                    <div className="border-t border-zinc-800 p-4 space-y-3">
                      {sub.type === 'trade' ? (
                        <div className="space-y-3">
                          {/* Exchange Cards - Stack on mobile */}
                          <div className="space-y-2">
                            {/* They Want */}
                            <div className="bg-zinc-800/50 p-3 flex items-center gap-3">
                              {sub.product_image && (
                                <img src={getImageUrl(sub.product_image)} alt="" className="w-14 h-14 object-cover flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] text-teal-400 font-mono">THEY WANT</p>
                                <p className="font-mono text-sm uppercase truncate">{sub.product_name}</p>
                                {sub.variant_name && (
                                  <p className="font-mono text-xs text-purple-400 uppercase truncate">{sub.variant_name}</p>
                                )}
                                <p className="text-base font-bold font-mono">${(sub.product_price ?? 0).toFixed(0)}</p>
                              </div>
                            </div>

                            {/* Arrow */}
                            <div className="text-center text-zinc-600 text-sm font-mono">↓ IN EXCHANGE FOR ↓</div>

                            {/* They're Giving */}
                            <div className="bg-purple-500/10 border border-purple-500/30 p-3 flex items-center gap-3">
                              {sub.item?.images?.[0] && (
                                <img 
                                  src={getImageUrl(sub.item.images[0])} 
                                  alt="" 
                                  className="w-14 h-14 object-cover flex-shrink-0 cursor-pointer"
                                  onClick={() => setLightboxData({ images: sub.item.images, initialIndex: 0 })}
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] text-purple-400 font-mono">THEIR ITEM</p>
                                <p className="font-mono text-sm uppercase truncate">{sub.item?.name}</p>
                                <p className="text-xs text-zinc-400 font-mono truncate">{sub.item?.description}</p>
                              </div>
                            </div>
                          </div>

                          {/* Summary Line */}
                          <div className="flex items-center justify-between text-sm font-mono py-2 border-t border-zinc-800">
                            <span>
                              <span className="text-purple-400">${(sub.item?.estimated_value ?? 0).toFixed(0)}</span>
                              <span className="text-zinc-600 mx-1">+</span>
                              <span className="text-green-400 font-bold">${(sub.cash_offer ?? 0).toFixed(0)} CASH</span>
                            </span>
                            <span className={`font-bold ${profitData?.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {profitData?.profit >= 0 ? '+' : ''}${(profitData?.profit ?? 0).toFixed(0)} PROFIT
                            </span>
                          </div>

                          {/* More Photos */}
                          {sub.item?.images?.length > 1 && (
                            <div className="flex gap-1 flex-wrap">
                              {sub.item.images.map((img, idx) => (
                                <img 
                                  key={idx}
                                  src={getImageUrl(img)}
                                  alt=""
                                  className="w-10 h-10 object-cover cursor-pointer hover:opacity-80"
                                  onClick={() => setLightboxData({ images: sub.item.images, initialIndex: idx })}
                                />
                              ))}
                            </div>
                          )}

                          {sub.counter_offer && (
                            <div className="bg-yellow-500/20 border border-yellow-500/50 p-3 text-center">
                              <p className="text-sm text-yellow-400 font-mono">YOUR COUNTER OFFER: ${sub.counter_offer.toFixed(0)}</p>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <ActionButtons
                              currentStatus={sub.status}
                              onAction={updateStatus}
                              submissionId={sub.id}
                              askingPrice={sub.product_price}
                              currentCashOffer={sub.counter_offer ?? sub.cash_offer}
                              isTrade
                            />
                            {/* Show CREATE PRODUCT button for accepted trade items */}
                            {sub.status === 'accepted' && sub.item && (
                              <button 
                                onClick={() => createProductFromItem(sub.item, sub.cash_offer || sub.item?.estimated_value)}
                                className="px-3 py-2 bg-teal-500 text-black font-mono text-xs font-bold flex items-center gap-1 hover:bg-teal-400 transition-colors"
                              >
                                <Plus size={14} /> CREATE PRODUCT
                              </button>
                            )}
                            {/* MARK AS RECEIVED toggle for accepted trades */}
                            {sub.status === 'accepted' && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() => markReceived(sub.id, null, !sub.trade_received, false)}
                                  className={`px-3 py-2 font-mono text-xs font-bold flex items-center gap-1 transition-colors ${sub.trade_received ? 'bg-green-500 text-black hover:bg-green-400' : 'bg-yellow-500 text-black hover:bg-yellow-400'}`}
                                  data-testid={`trade-mark-received-${sub.id}`}
                                >
                                  {sub.trade_received ? '✓ RECEIVED — UNDO' : 'MARK AS RECEIVED'}
                                </button>
                                {!sub.trade_received && (
                                  <button
                                    onClick={() => markReceived(sub.id, null, true, true)}
                                    className="px-2 py-2 font-mono text-[10px] text-zinc-500 hover:text-yellow-400 underline"
                                    title="Mark received WITHOUT sending the user an email — for backfilling old trades"
                                    data-testid={`trade-mark-received-silent-${sub.id}`}
                                  >
                                    (NO EMAIL)
                                  </button>
                                )}
                                {sub.trade_received && sub.trade_received_at && (
                                  <span className="text-[10px] font-mono text-zinc-500">
                                    {new Date(sub.trade_received_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <button onClick={() => setDeleteConfirm(sub.id)} className="text-red-400 text-sm font-mono flex items-center gap-1">
                            <Trash2 size={14} /> DELETE
                          </button>
                        </div>
                      ) : (
                        /* Sell Items */
                        <div className="space-y-4">
                          {/* PayPal Email - 1-click copy */}
                          {sub.paypal_email && (
                            <div 
                              onClick={() => copyToClipboard(sub.paypal_email)}
                              className="bg-green-500/10 border border-green-500/30 p-3 flex items-center justify-between cursor-pointer hover:bg-green-500/20 transition-colors"
                            >
                              <div>
                                <p className="text-[10px] text-green-400 font-mono">PAYPAL EMAIL (TAP TO COPY)</p>
                                <p className="font-mono text-sm text-green-300">{sub.paypal_email}</p>
                              </div>
                              <span className="text-green-400 text-xs font-mono">📋</span>
                            </div>
                          )}
                          
                          {sub.items?.map(item => (
                            <div key={item.item_id} className="bg-zinc-800 p-4">
                              <div className="flex justify-between items-start gap-3 mb-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <p className="font-mono text-base uppercase font-medium">{item.name}</p>
                                    {renderStatusBadge(item.status)}
                                    {item.status === 'accepted' && (
                                      <span
                                        className={`px-2 py-1 text-[10px] font-mono font-bold border ${item.received ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'}`}
                                        title={item.received_at ? new Date(item.received_at).toLocaleString() : ''}
                                      >
                                        {item.received
                                          ? `✓ RECEIVED ${item.received_at ? '· ' + new Date(item.received_at).toLocaleDateString(undefined,{month:'short',day:'numeric'}) : ''}`
                                          : 'PENDING RECEIVING'}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-zinc-400 font-mono">{item.description}</p>
                                  
                                  {/* Category & Brand */}
                                  <div className="flex gap-3 mt-2 text-sm font-mono">
                                    <span className="text-teal-400">ASKING: ${(item.asking_price ?? 0).toFixed(2)}</span>
                                    {item.category && <span className="text-zinc-500">{item.category.toUpperCase()}</span>}
                                    {item.brand && <span className="text-zinc-500">{item.brand.toUpperCase()}</span>}
                                  </div>
                                  
                                  {item.counter_offer && (
                                    <p className="text-sm text-yellow-400 font-mono mt-2">
                                      YOUR OFFER: ${item.counter_offer.toFixed(2)}
                                    </p>
                                  )}

                                  {item.images?.length > 0 && (
                                    <div className="flex gap-2 mt-3">
                                      {item.images.map((img, idx) => (
                                        <img 
                                          key={idx}
                                          src={getImageUrl(img)}
                                          alt=""
                                          className="w-14 h-14 object-cover cursor-pointer hover:opacity-80"
                                          onClick={() => setLightboxData({ images: item.images, initialIndex: idx })}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Actions for this item */}
                              <div className="pt-3 border-t border-zinc-700 flex items-center gap-3 flex-wrap">
                                <ActionButtons
                                  currentStatus={item.status}
                                  onAction={updateStatus}
                                  submissionId={sub.id}
                                  itemId={item.item_id}
                                  askingPrice={item.asking_price}
                                  currentCashOffer={item.counter_offer ?? item.asking_price}
                                />
                                {/* Show CREATE PRODUCT button for accepted items */}
                                {item.status === 'accepted' && (
                                  <button 
                                    onClick={() => createProductFromItem(item, item.counter_offer || item.asking_price)}
                                    className="px-3 py-2 bg-teal-500 text-black font-mono text-xs font-bold flex items-center gap-1 hover:bg-teal-400 transition-colors"
                                  >
                                    <Plus size={14} /> CREATE PRODUCT
                                  </button>
                                )}
                                {/* MARK AS RECEIVED toggle for accepted sell items */}
                                {item.status === 'accepted' && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                      onClick={() => markReceived(sub.id, item.item_id, !item.received, false)}
                                      className={`px-3 py-2 font-mono text-xs font-bold flex items-center gap-1 transition-colors ${item.received ? 'bg-green-500 text-black hover:bg-green-400' : 'bg-yellow-500 text-black hover:bg-yellow-400'}`}
                                    >
                                      {item.received ? '✓ RECEIVED — UNDO' : 'MARK AS RECEIVED'}
                                    </button>
                                    {!item.received && (
                                      <button
                                        onClick={() => markReceived(sub.id, item.item_id, true, true)}
                                        className="px-2 py-2 font-mono text-[10px] text-zinc-500 hover:text-yellow-400 underline"
                                        title="Mark received WITHOUT sending the user an email"
                                      >
                                        (NO EMAIL)
                                      </button>
                                    )}
                                    {item.received && item.received_at && (
                                      <span className="text-[10px] font-mono text-zinc-500">
                                        {new Date(item.received_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          <button onClick={() => setDeleteConfirm(sub.id)} className="text-red-400 text-sm font-mono flex items-center gap-1">
                            <Trash2 size={14} /> DELETE SUBMISSION
                          </button>
                        </div>
                      )}

                      {sub.notes && (
                        <p className="text-sm text-zinc-500 font-mono border-t border-zinc-700 pt-3">NOTE: {sub.notes}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissions;
